import { appendDpiaFilterQueryParams } from 'config/api/assetRopa/assetRopaDpiaFilters';
import type {
  AssetProcessingActivityList,
  AssetProcessingActivityListParams,
} from 'config/api/processingActivities/processingActivities.types';

import type { AssetProcessingActivityTableFilters, AssetProcessingActivityTableSort } from './processingActivityDetail.types';

const SORT_FIELD_MAP: Record<string, string> = {
  activePurposesCount: 'active_purposes_count',
  assetName: 'asset_name',
  dpiaStatus: 'dpia_status',
  hasSensitivePersonalData: 'has_sensitive_personal_data',
  officialReviewStatus: 'official_review_status',
  operationalOwnerDisplayName: 'operational_owner_display_name',
  updatedAt: 'updated_at',
};

const parseBooleanFilter = (value: string): boolean | undefined => {
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return undefined;
};

export const buildAssetProcessingActivitySortParam = (sort: AssetProcessingActivityTableSort): string | undefined => {
  if (!sort.key || !sort.direction) {
    return undefined;
  }

  const field = SORT_FIELD_MAP[sort.key];
  if (!field) {
    return undefined;
  }

  return sort.direction === 'desc' ? `-${field}` : field;
};

export const buildAssetProcessingActivityListParams = (
  filters: AssetProcessingActivityTableFilters,
  sort: AssetProcessingActivityTableSort,
): AssetProcessingActivityListParams => {
  const params: AssetProcessingActivityListParams = {};

  if (filters.officialReviewStatus) {
    params.official_review_status = filters.officialReviewStatus;
  }

  const hasSensitivePersonalData = parseBooleanFilter(filters.hasSensitivePersonalData);
  if (hasSensitivePersonalData != null) {
    params.has_sensitive_personal_data = hasSensitivePersonalData;
  }

  const sortParam = buildAssetProcessingActivitySortParam(sort);
  if (sortParam) {
    params.sort = sortParam;
  }

  return appendDpiaFilterQueryParams(params, filters);
};

const compareStrings = (left: string, right: string): number => left.localeCompare(right, undefined, { sensitivity: 'base' });

const compareNumbers = (left: number, right: number): number => left - right;

const compareBooleans = (left: boolean, right: boolean): number => Number(left) - Number(right);

const getSortValue = (row: AssetProcessingActivityList, key: string): string | number | boolean => {
  switch (key) {
    case 'assetName':
      return row.assetName;
    case 'operationalOwnerDisplayName':
      return row.operationalOwnerDisplayName;
    case 'officialReviewStatus':
      return row.officialReviewStatus ?? '';
    case 'updatedAt':
      return row.updatedAt;
    case 'dpiaStatus':
      return row.dpiaStatus;
    case 'activePurposesCount':
      return row.activePurposesCount;
    case 'hasSensitivePersonalData':
      return row.hasSensitivePersonalData;
    default:
      return row.assetName;
  }
};

const matchesDpiaFilters = (row: AssetProcessingActivityList, filters: AssetProcessingActivityTableFilters): boolean => {
  if (filters.dpiaIndication === 'false') {
    return row.dpiaStatus === 'not_indicated';
  }

  if (filters.dpiaIndication === 'true' && row.dpiaStatus === 'not_indicated') {
    return false;
  }

  if (filters.dpiaRequestStatus) {
    if (row.dpiaRequestStatus !== filters.dpiaRequestStatus) {
      return false;
    }
  }

  if (filters.dpiaStatus) {
    if (row.dpiaStatus !== filters.dpiaStatus) {
      return false;
    }
  }

  return true;
};

export const filterAssetProcessingActivities = (
  rows: AssetProcessingActivityList[],
  filters: AssetProcessingActivityTableFilters,
): AssetProcessingActivityList[] =>
  rows.filter(row => {
    if (filters.officialReviewStatus && row.officialReviewStatus !== filters.officialReviewStatus) {
      return false;
    }

    if (!matchesDpiaFilters(row, filters)) {
      return false;
    }

    if (filters.hasSensitivePersonalData) {
      const expected = filters.hasSensitivePersonalData === 'true';
      if (row.hasSensitivePersonalData !== expected) {
        return false;
      }
    }

    return true;
  });

export const sortAssetProcessingActivities = (
  rows: AssetProcessingActivityList[],
  sort: AssetProcessingActivityTableSort,
): AssetProcessingActivityList[] => {
  if (!sort.key || !sort.direction) {
    return rows;
  }

  const directionMultiplier = sort.direction === 'asc' ? 1 : -1;

  return [...rows].sort((left, right) => {
    const leftValue = getSortValue(left, sort.key as string);
    const rightValue = getSortValue(right, sort.key as string);

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return compareNumbers(leftValue, rightValue) * directionMultiplier;
    }

    if (typeof leftValue === 'boolean' && typeof rightValue === 'boolean') {
      return compareBooleans(leftValue, rightValue) * directionMultiplier;
    }

    return compareStrings(String(leftValue), String(rightValue)) * directionMultiplier;
  });
};

export const hasAssetProcessingActivityFiltersApplied = (filters: AssetProcessingActivityTableFilters): boolean =>
  filters.officialReviewStatus !== '' ||
  filters.dpiaIndication !== '' ||
  filters.dpiaRequestStatus !== '' ||
  filters.dpiaStatus !== '' ||
  filters.hasSensitivePersonalData !== '';
